import React, { useEffect, useState } from 'react';
import { Loader2, FileText, ArrowLeft, ShieldCheck } from 'lucide-react';
import {
  getRequired,
  getDocument,
  acceptDocument,
  type RequiredDoc,
  type LegalDocVersion,
} from '@/services/complianceApi';
import { getApiErrorMessage } from '@/lib/apiError';

/**
 * Blocks the authenticated app until the user has accepted every required legal
 * document at its current version. This is the universal acceptance gate for BOTH
 * company admins (first login after registration) and invited employees (first
 * login), and it also forces re-acceptance when a policy version changes.
 *
 * Fail-open: if compliance-service is unreachable or returns nothing, the app loads
 * normally (availability over a hard lock).
 */
const ComplianceGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [required, setRequired] = useState<RequiredDoc[]>([]);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [viewing, setViewing] = useState<LegalDocVersion | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    getRequired()
      .then((r) => active && setRequired(r))
      .catch(() => active && setRequired([])) // fail-open
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  if (loading || required.length === 0) {
    return <>{children}</>;
  }

  const allChecked = required.every((d) => checked[d.doc_type]);

  const view = async (docType: string) => {
    try {
      setViewing(await getDocument(docType));
    } catch {
      setError('Could not load the document. Please try again.');
    }
  };

  const accept = async () => {
    setSubmitting(true);
    setError(null);
    try {
      for (const d of required) {
        await acceptDocument(d.doc_type, d.version);
      }
      setRequired([]); // accepted → reveal the app
    } catch (e) {
      setError(getApiErrorMessage(e, 'We could not record your acceptance. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-charcoal/60 p-4 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-2xl bg-card shadow-2xl">
        {viewing ? (
          <div className="flex max-h-[85vh] flex-col">
            <div className="flex items-center gap-2 border-b border-border px-6 py-4">
              <button
                onClick={() => setViewing(null)}
                className="flex items-center gap-1 text-sm font-medium text-orange-600 hover:text-orange-700"
              >
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              <h2 className="ml-2 truncate text-base font-bold text-foreground">{viewing.title}</h2>
            </div>
            <div className="overflow-y-auto px-6 py-5 text-sm leading-relaxed text-muted-foreground">
              <pre className="whitespace-pre-wrap font-sans">{viewing.body}</pre>
            </div>
          </div>
        ) : (
          <div className="p-6">
            <div className="mb-4 flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-100 text-orange-600 dark:bg-orange-500/15">
                <ShieldCheck className="h-5 w-5" />
              </span>
              <div>
                <h2 className="text-lg font-bold text-foreground">Before you continue</h2>
                <p className="text-sm text-muted-foreground">
                  Please review and accept the following to use KaziPeople.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              {required.map((d) => (
                <label
                  key={d.doc_type}
                  className="flex cursor-pointer items-center gap-3 rounded-xl border border-border p-3 hover:bg-muted/50"
                >
                  <input
                    type="checkbox"
                    checked={!!checked[d.doc_type]}
                    onChange={(e) => setChecked((c) => ({ ...c, [d.doc_type]: e.target.checked }))}
                    className="h-4 w-4 accent-orange-500"
                  />
                  <FileText className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                  <span className="flex-1 text-sm font-medium text-foreground">
                    I accept the {d.title}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      view(d.doc_type);
                    }}
                    className="text-xs font-semibold text-orange-600 hover:text-orange-700"
                  >
                    Read
                  </button>
                </label>
              ))}
            </div>

            {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

            <button
              onClick={accept}
              disabled={!allChecked || submitting}
              className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Accept and continue
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComplianceGate;
